import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScrollText, ArrowRight } from "lucide-react";
import { API } from "@/App";
import { toArabicNumber } from "@/data/quranMeta";

/**
 * Daily ayah-of-the-day card with tafsir excerpt. Same verse for all users on
 * the same UTC date (deterministic on the backend). Click → opens the verse
 * in Flashcard mode with tafsir auto-enabled.
 */
export default function TafsirOfTheDay() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/tafsir/daily`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        if (d && d.tafsir) setData(d);
        else setError(true);
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  if (error || !data) return null;

  // Truncate tafsir to ~280 chars so the card stays compact.
  const TAF_LIMIT = 280;
  const tafExcerpt =
    data.tafsir && data.tafsir.length > TAF_LIMIT
      ? data.tafsir.slice(0, TAF_LIMIT).replace(/\s+\S*$/, "") + "…"
      : data.tafsir;

  // When clicking through, we want the Reader to open with tafsir on. The
  // Reader reads localStorage on mount, so we set the flag here. The actual
  // ayah jump is driven by the ?ayah=N URL param.
  const handleOpen = () => {
    try {
      localStorage.setItem("quran_show_tafsir", "1");
    } catch (err) {
      console.warn("localStorage disabled (private mode?):", err);
    }
  };

  return (
    <section
      data-testid="tafsir-of-the-day-card"
      className="glass-card rounded-xl p-6 mb-10 card-lift"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gold-400/10 flex items-center justify-center shrink-0">
            <ScrollText className="w-5 h-5 text-gold-400" />
          </div>
          <div>
            <p className="font-cinzel text-slate-100 text-base">
              Tafsir of the Day
            </p>
            <p className="text-slate-500 text-xs font-outfit">
              {data.surah_english || `Surah ${data.surah}`} ·{" "}
              {toArabicNumber(data.surah)}:{toArabicNumber(data.ayah)} ·{" "}
              {data.edition_name || "Tafhim al-Quran"}
            </p>
          </div>
        </div>
      </div>

      {data.arabic && (
        <p
          className="font-noto-naskh text-slate-100 text-2xl leading-[2.2] text-right mb-4"
          style={{ direction: "rtl" }}
          data-testid="totd-arabic"
        >
          {data.arabic}
        </p>
      )}

      {data.translation && (
        <p className="text-slate-300 font-outfit italic text-sm mb-4 leading-relaxed">
          “{data.translation}”
        </p>
      )}

      <p
        className="text-slate-400 font-outfit text-sm leading-relaxed border-l-2 border-gold-400/30 pl-4"
        data-testid="totd-tafsir"
      >
        {tafExcerpt}
      </p>

      <div className="mt-5 flex justify-end">
        <Link
          to={`/reader/${data.surah}?mode=flashcard&ayah=${data.ayah}`}
          onClick={handleOpen}
          data-testid="totd-open-link"
          className="inline-flex items-center gap-1.5 text-gold-400 hover:text-gold-300 font-outfit text-sm transition-colors"
        >
          Read full tafsir
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}
